import React, { useEffect, useContext } from "react";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import { getSettingsDetails, updateSettings } from "../api";
import { getAllUsers } from "../../EmailSettings/api";
import qs from "qs";
import { useProductSort } from "../../ProductSorting";
import { AppContext } from "../../../store";
import { useLocation } from "react-router-dom";

export const useSettings = (storeId = null) => {
  const { state: stateTemplates } = useProductSort({ isList: true });
  const { pathname } = useLocation();
  const { appState } = useContext(AppContext);
  const isStoreAdmin = pathname.includes("store-admin");

  const { globalData } = appState;
  const [state, setState] = useImmer({
    isSaveButton: true,
    isLoading: true,
    userList: [],
    details: {
      variance_status: 0,
      eod_variance: null,
      auto_log_status: 0,
      auto_log_time: null,
      email: [],
      float_amount: null,
      keypad_id: "",
      delivery: 0,
      delivery_provider: "",
      delivery_radius: "",
      pre_order_days: "",
      eod_template: "",
      cart_product_count_limit: "",
      cart_product_waiting_time_limit: "",
      local_kds: 0,
      dt_pick_up_available: 0,
      // Franchisee new update
      missed_financial_mails: [],
      discrepancy_alert_mails: [],
      ytd_cal_ytd_variance_mails: [],
      // alert_for_account_file_overwrite: [],
    },
    input: "",
  });

  useEffect(() => {
    if (storeId && state.isLoading) {
      HandleSettingsDetails(storeId);
    }
  }, [storeId]);

  const HandleSettingsDetails = async (storeId) => {
    try {
      const res = await getSettingsDetails(storeId);
      if (res.success) {
        setState((draft) => {
          draft.details = res.data;
          draft.isLoading = false;
        });
      }
    } catch (error) {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const HandleInputChange = (event) => {
    const { value, name } = event.target;
    setState((draft) => {
      draft.details[name] = value;
      draft.isSaveButton = false;
    });
  };

  const getUserIdsAndEmails = async (userList) => {
    const details = {
      ids: [],
      emails: [],
    };
    userList.forEach((user) => {
      if (user.id) details.ids.push(user.id);
      else if (user.email) details.emails.push(user.email);
    });

    return details;
  };

  const onUpdateSettings = async (id) => {
    try {
      setState((draft) => {
        draft.isSaveButton = true;
      });

      const eodSummaryUsers = await getUserIdsAndEmails(state.details.email);

      const missedFinancialUsers = await getUserIdsAndEmails(
        state.details.missed_financial_mails
      );
      const discrepancyAlertUsers = await getUserIdsAndEmails(
        state.details.discrepancy_alert_mails
      );
      const ytdCalYtdVarianceUsers = await getUserIdsAndEmails(
        state.details.ytd_cal_ytd_variance_mails
      );

      const payload = {
        ...state.details,
        email: eodSummaryUsers.emails,
        user_id: eodSummaryUsers.ids,
        missed_financial_mails: missedFinancialUsers.emails,
        missed_financial_user_id: missedFinancialUsers.ids,
        discrepancy_mails: discrepancyAlertUsers.emails,
        discrepancy_user_id: discrepancyAlertUsers.ids,
        ytd_cal_ytd_variance_mails: ytdCalYtdVarianceUsers.emails,
        ytd_cal_ytd_variance_user_id: ytdCalYtdVarianceUsers.ids,
      };

      delete payload.discrepancy_alert_mails;

      if (!payload.auto_log_status && !payload.auto_log_time) {
        delete payload.auto_log_time;
      }
      if (!payload.variance_status && !payload.eod_variance) {
        delete payload.eod_variance;
      }
      if (isStoreAdmin) {
        delete payload.delivery;
        delete payload.delivery_provider;
        delete payload.delivery_radius;
        delete payload.pre_order_days;
      }
      const res = await updateSettings(storeId, payload);
      if (res.success) {
        successMessage(res.message);
        setState((draft) => {
          draft.isSaveButton = false;
        });
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  const handleAddMaIL = ({ input, type }) => {
    const data = [
      ...state.details[type],
      {
        email: input,
        name: null,
        id: null,
      },
    ];
    setState((draft) => {
      draft.details[type] = data;
      draft.isSaveButton = false;
    });
  };

  const handleDeleteMaIL = ({ type, email }) => {
    setState((draft) => {
      draft.details[type] = [
        ...draft.details[type].filter((data) => {
          return data.email !== email;
        }),
      ];
      draft.isSaveButton = false;
    });
  };

  const handleGetUserList = async ({ type, search = null }) => {
    let params = {};

    if (search) {
      params = { ...params, search };
    }
    const query = {
      params,
      headers: { "X-STORE": storeId },
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      const res = await getAllUsers(query);
      if (res.success) {
        setState((draft) => {
          draft.userList = res.data.map((data) => {
            return { value: data.id, label: data.name };
          });
          draft.isBusy = false;
        });
        const selected = state.details[type].map((v) => v.id).filter(Boolean);
        const filteredData = res.data.filter((v) => !selected.includes(v.id));
        return {
          options: filteredData.map((data) => {
            return {
              value: data.id,
              id: data.id,
              name: data.name,
              email: data.email,
            };
          }),
        };
      }
    } catch (err) {
      return {
        options: [],
      };
    }
  };

  const handleOnChange = (e, name) => {
    setState((draft) => {
      draft.details[name] = e?.value;
      draft.isSaveButton = false;
    });
  };

  return {
    state,
    globalData,
    HandleInputChange,
    onUpdateSettings,
    handleAddMaIL,
    handleDeleteMaIL,
    handleGetUserList,
    stateTemplates,
    handleOnChange,
  };
};
